import { Form } from '@mairistem/composants';
import {
	type Exercice,
	type LigneMouvement,
	type Mouvement,
	MULTI,
	type Rib,
	type Sens,
	type Tiers as TiersInterface,
} from '@mairistem/comptabilite';
import { isEmpty, isNil } from 'lodash';
import React from 'react';
import type BaseProfilEcritureMouvement from '../../../../classes/mouvements/profilEcriture/Base/BaseProfilEcritureMouvement';
import { isLibelleReferenceVisible } from '../../../../constants/mouvements/modeReglements';
import type PanelData from '../../../../interfaces/pieces/mouvements/panelData';
import getRibChanges, { emptyRib } from '../../../../utils/mouvements/rib';
import ReferencesBancaire from '../../Common/ReferencesBancaire';
import TiersInformations from '../../Common/TiersInformations';
import { getDureeFromTiersOrExercice } from '../Complements/DelaiPaiement/delaiPaiementUtils';

const RIB_MULTI = {
	bic: MULTI,
	defaut: true,
	iban: '',
	identifiant: MULTI,
	identifiantTiers: 0,
	titulaire: '',
} as Rib;

interface TiersProps {
	exercice: Exercice;
	isTiersMulti: boolean;
	onChange: (
		ligneData: Partial<LigneMouvement>,
		mouvementData?: Pick<Mouvement, 'dureeDelaiGlobalPaiement'>,
	) => void;
	panelData: PanelData;
	profilEcriture: BaseProfilEcritureMouvement;
	sens: string;
	tiers: TiersInterface;
}

const Tiers = ({
	exercice,
	isTiersMulti,
	onChange,
	panelData,
	profilEcriture,
	sens,
	tiers,
}: TiersProps) => {
	const handleTiersReset = () => {
		onChange({
			identifiantTiers: null,
			modeReglement: null,
			tiers: null,
		});
	};
	const handleTiersChange = (t: TiersInterface) => {
		if (!isNil(t)) {
			const modeReglement =
				profilEcriture?.defaultModeReglement ?? t?.modeReglementDepense;
			const rib = t?.ribs.find((r) => r.defaut);
			const keepRib = isLibelleReferenceVisible(modeReglement, sens as Sens);
			onChange(
				{
					identifiantTiers: Number(t?.identifiant),
					modeReglement,
					...(keepRib && rib ? getRibChanges(rib) : emptyRib),
					tiers: t,
				},
				getDureeFromTiersOrExercice(exercice, t),
			);
		}
	};

	const handleChange = (_e, { name, value }) => {
		onChange({ [name]: value });
	};

	const handleRibChange = (rib: Rib) => {
		// Si on n'est pas sur le rib MULTI
		if (rib?.identifiant.toString() !== MULTI) {
			onChange(getRibChanges(rib));
		}
	};

	return (
		<Form>
			<TiersInformations
				adresse={tiers?.adresses?.[0]}
				modeReglement={panelData?.modeReglement ?? ''}
				onChange={onChange}
				onReset={handleTiersReset}
				onTiersChange={handleTiersChange}
				placeholderModeReglement={
					panelData?.modeReglement === MULTI ? MULTI : ''
				}
				placeholderTiersInput={isTiersMulti ? MULTI : ''}
				profilEcriture={profilEcriture}
				sens={sens}
				tiers={tiers}
			/>
			{isLibelleReferenceVisible(panelData?.modeReglement, sens as Sens) && (
				<ReferencesBancaire
					identifiantRib={
						panelData?.iban === MULTI
							? MULTI
							: tiers?.ribs?.find((rib) => rib.iban === panelData?.iban)
									?.identifiant
					}
					libelleVirementZoneLibre={panelData?.libelleVirementZoneLibre}
					libelleVirementZonLibre={panelData?.libelleVirementZonLibre}
					onChange={handleChange}
					onRibChange={handleRibChange}
					ribs={[
						...(!isTiersMulti && !isEmpty(tiers?.ribs)
							? (tiers?.ribs ?? [])
							: []),
						...(panelData?.iban === MULTI ? [RIB_MULTI] : []),
					]}
				/>
			)}
		</Form>
	);
};

export default Tiers;
